import {
  COLORS,
  ChatIconButton,
  RADIUS,
  SHADOW,
  SPACE,
  STALE_TIME,
  TYPE,
} from "@gtm-agents/gtm-ui";
import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { useChatContext } from "../context/ChatContext.js";
import { useView } from "../context/ViewContext.js";
import { getStoredUser, isAdmin, logout } from "../lib/auth.js";
import { trpc } from "../lib/trpc.js";

type NavItem = { to: string; label: string; end?: boolean; adminOnly?: boolean };

type NavGroup = {
  label: string;
  items: NavItem[];
  adminOnly?: boolean;
};

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Home",
    items: [{ to: "/", label: "Today" }],
  },
  {
    label: "Pipeline",
    items: [
      { to: "/pipeline", label: "Pipeline" },
      { to: "/forecast", label: "Forecast", adminOnly: true },
    ],
  },
  {
    label: "Growth",
    items: [
      { to: "/leads", label: "Leads" },
      { to: "/prospecting", label: "Prospecting" },
    ],
  },
  // Marketing section omitted — not yet implemented

  {
    label: "Customers",
    adminOnly: true,
    items: [
      { to: "/customers", label: "Overview", end: true },
      { to: "/customers/onboarding", label: "Onboarding" },
      { to: "/customers/renewals", label: "Renewals" },
      { to: "/customers/at-risk", label: "At Risk" },
      { to: "/customers/feedback", label: "Feedback" },
    ],
  },
  {
    label: "Accounts",
    items: [
      { to: "/companies", label: "Companies" },
      { to: "/contacts", label: "Contacts" },
    ],
  },
  {
    label: "Partners",
    adminOnly: true,
    items: [{ to: "/partners", label: "Partners" }],
  },
  {
    label: "Work",
    items: [
      { to: "/tasks", label: "Tasks" },
      { to: "/documents", label: "Documents", adminOnly: true },
    ],
  },
  {
    label: "Revenue",
    adminOnly: true,
    items: [
      { to: "/revenue", label: "Revenue" },
      { to: "/revenue/targets", label: "Targets" },
    ],
  },
  {
    label: "People",
    adminOnly: true,
    items: [
      { to: "/people/hiring", label: "Hiring" },
      { to: "/people/onboarding", label: "Team Onboarding" },
      { to: "/people/team", label: "Team" },
      { to: "/people/capacity", label: "Capacity" },
    ],
  },
  {
    label: "Ops",
    adminOnly: true,
    items: [
      { to: "/ops/agents", label: "Agents" },
      { to: "/ops/system", label: "System" },
      { to: "/ops/observability", label: "Observability" },
    ],
  },
  {
    label: "Settings",
    adminOnly: true,
    items: [
      { to: "/settings/profile", label: "Profile" },
      { to: "/settings/integrations", label: "Integrations" },
      { to: "/settings/team", label: "Team Admin" },
      { to: "/settings/health", label: "Health Config" },
      { to: "/settings/trash", label: "Trash" },
      { to: "/settings/retention", label: "Retention" },
    ],
  },
];

const externalLinks = [
  { href: "https://app.commonroom.io", label: "Common Room" },
  { href: "https://app.clay.com", label: "Clay" },
  { href: "https://app.instantly.ai", label: "Instantly" },
  { href: "https://linear.app", label: "Linear" },
  { href: "https://app.axiom.co", label: "Axiom" },
];

// Brand-active is a stronger tint than COLORS.brandSoft (0.12) — used for selected toggle state
const BRAND_ACTIVE = "rgba(124,58,237,0.3)";
// Nav sidebar has a slightly darker bg than the page bg
const NAV_BG = "#0e0e18";
// Dropdown popover bg — slightly lighter than NAV_BG for depth
const NAV_DROPDOWN_BG = "#1a1a2e";
// Sub-xs font sizes for micro-elements (carets, avatar glyphs) — no theme token this small
const FONT_MICRO = TYPE.size.xs - 2; // 9px
const FONT_NANO = TYPE.size.xs - 3; // 8px

const S = {
  nav: {
    width: 220,
    minWidth: 220,
    background: NAV_BG,
    borderRight: `1px solid ${COLORS.border}`,
    display: "flex",
    flexDirection: "column" as const,
    height: "100vh",
    position: "sticky" as const,
    top: 0,
    overflowY: "auto" as const,
    padding: `${SPACE[6]}px 0`,
  },
  logo: {
    padding: `0 ${SPACE[5]}px ${SPACE[5]}px`,
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: SPACE[2],
    flexShrink: 0,
  },
  groupLabel: {
    fontSize: TYPE.size.xs - 1,
    fontWeight: TYPE.weight.semibold,
    color: COLORS.textDim,
    letterSpacing: TYPE.letter.widest,
    textTransform: "uppercase" as const,
    padding: `${SPACE[3]}px ${SPACE[5]}px ${SPACE[1]}px`,
  },
  groupItems: { padding: `0 ${SPACE[3]}px`, marginBottom: SPACE[1] },
  externalSection: {
    padding: `0 ${SPACE[3]}px`,
    marginTop: "auto",
    borderTop: `1px solid ${COLORS.border}`,
    paddingTop: SPACE[4],
    flexShrink: 0,
  },
  toggle: {
    display: "flex",
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.base,
    padding: 2,
    gap: 2,
  } as const,
  toggleBtn: (active: boolean) =>
    ({
      flex: 1,
      fontSize: TYPE.size.xs,
      fontWeight: active ? TYPE.weight.semibold : TYPE.weight.normal,
      borderRadius: RADIUS.sm,
      border: "none",
      background: active ? BRAND_ACTIVE : "transparent",
      color: active ? COLORS.brandText : COLORS.textMuted,
      cursor: "pointer",
      transition: `all ${120}ms`,
      fontFamily: TYPE.family.sans,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: SPACE[1],
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      padding: `${SPACE[1]}px ${SPACE[1] + 2}px`,
    }) as React.CSSProperties,
  dropdown: {
    position: "absolute" as const,
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: NAV_DROPDOWN_BG,
    border: `1px solid ${COLORS.borderHigh}`,
    borderRadius: RADIUS.md,
    overflow: "hidden" as const,
    zIndex: 100,
    boxShadow: SHADOW.md,
  },
  dropdownBtn: (active: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      gap: SPACE[2],
      width: "100%",
      padding: `${SPACE[2]}px ${SPACE[3]}px`,
      background: active ? COLORS.brandSoft : "transparent",
      border: "none",
      borderBottom: `1px solid ${COLORS.surface}`,
      color: active ? COLORS.brandText : COLORS.textMuted,
      fontSize: TYPE.size.sm,
      cursor: "pointer",
      textAlign: "left",
      fontFamily: TYPE.family.sans,
    }) as React.CSSProperties,
  avatar: {
    width: 20,
    height: 20,
    borderRadius: RADIUS.full,
    background: COLORS.brandSoft,
    border: "1px solid rgba(124,58,237,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: FONT_MICRO,
    fontWeight: TYPE.weight.bold,
    color: COLORS.brandText,
    flexShrink: 0,
  },
  navLink: (isActive: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      padding: `${SPACE[1] + 2}px ${SPACE[2] + 2}px`,
      borderRadius: RADIUS.base,
      color: isActive ? COLORS.brandText : COLORS.textMuted,
      background: isActive ? COLORS.brandSoft : "transparent",
      textDecoration: "none",
      fontSize: TYPE.size.base,
      fontWeight: isActive ? TYPE.weight.medium : TYPE.weight.normal,
      marginBottom: 1,
      transition: "all 100ms",
    }) as React.CSSProperties,
  extLink: {
    display: "flex",
    alignItems: "center",
    gap: SPACE[1] + 2,
    padding: `5px ${SPACE[2]}px`,
    borderRadius: RADIUS.base,
    color: COLORS.textMuted,
    textDecoration: "none",
    fontSize: TYPE.size.sm,
  } as const,
  signOut: {
    display: "flex",
    alignItems: "center",
    gap: SPACE[1] + 2,
    padding: `5px ${SPACE[2]}px`,
    borderRadius: RADIUS.base,
    color: COLORS.textMuted,
    background: "none",
    border: "none",
    fontSize: TYPE.size.sm,
    cursor: "pointer",
    width: "100%",
    textAlign: "left" as const,
    marginTop: SPACE[1],
    fontFamily: TYPE.family.sans,
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: SPACE[2] + 2,
    marginBottom: SPACE[1],
  } as const,
  logoIcon: {
    width: 32,
    height: 32,
    background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
    borderRadius: RADIUS.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: TYPE.size.md,
    fontWeight: TYPE.weight.bold,
    color: COLORS.white,
    flexShrink: 0,
  } as const,
  logoTitle: {
    fontSize: TYPE.size.md,
    fontWeight: TYPE.weight.semibold,
    color: COLORS.text,
    flex: 1,
  } as const,
  logoSubtitle: {
    fontSize: TYPE.size.xs,
    color: COLORS.textMuted,
    letterSpacing: TYPE.letter.wide,
    textTransform: "uppercase" as const,
    marginTop: 2,
  },
  userSection: {
    padding: `${SPACE[2]}px ${SPACE[5]}px ${SPACE[3]}px`,
    borderBottom: `1px solid ${COLORS.border}`,
    marginBottom: SPACE[1],
    flexShrink: 0,
  } as const,
  userRow: {
    display: "flex",
    alignItems: "center",
    gap: SPACE[2],
    marginBottom: SPACE[2] + 2,
  } as const,
  userAvatarLg: {
    width: 24,
    height: 24,
    borderRadius: RADIUS.full,
    background: COLORS.brandSoft,
    border: "1px solid rgba(124,58,237,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: TYPE.size.xs - 1,
    fontWeight: TYPE.weight.bold,
    color: COLORS.brandText,
    flexShrink: 0,
  } as const,
  userInfo: { minWidth: 0 } as const,
  userEmail: {
    fontSize: TYPE.size.sm,
    color: COLORS.textMuted,
    whiteSpace: "nowrap" as const,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
  },
  userRole: (leadership: boolean) =>
    ({
      fontSize: TYPE.size.xs,
      color: leadership ? COLORS.brandText : COLORS.textMuted,
      marginTop: 1,
      textTransform: "capitalize",
    }) as React.CSSProperties,
  toggleOwnerText: {
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    flex: 1,
    textAlign: "left" as const,
  },
  toggleCaret: { fontSize: FONT_NANO, opacity: 0.7, flexShrink: 0 } as const,
  dropdownTextSpan: {
    flex: 1,
    overflow: "hidden" as const,
    textOverflow: "ellipsis" as const,
    whiteSpace: "nowrap" as const,
  },
  dropdownCheck: { fontSize: TYPE.size.xs, color: COLORS.brandText } as const,
  navScrollArea: { flex: 1, overflow: "auto" as const },
  extLinkCaret: { fontSize: FONT_MICRO, opacity: 0.5 } as const,
  signOutIcon: { fontSize: TYPE.size.xs - 1, opacity: 0.5 } as const,
};

export function NavBar() {
  const user = getStoredUser();
  const { ownerUserId, setOwnerUserId } = useView();
  const { setChatOpen } = useChatContext();
  const isLeadership = user?.gtm_role === "leadership" || user?.gtm_role === "admin";
  const admin = isAdmin(user);
  const emailLabel = user?.email?.replace("@axiom.co", "") ?? null;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: usersData } = trpc.v1.crm.listWorkspaceUsers.useQuery(undefined, {
    staleTime: STALE_TIME.SLOW,
  });
  const workspaceUsers = usersData?.users ?? [];

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const { data: profile } = trpc.v1.settings.whoami.useQuery(undefined, {
    staleTime: STALE_TIME.SLOW,
    enabled: !!user,
  });

  const navInitials = (() => {
    const firstName = profile?.firstName ?? user?.firstName;
    const lastName = profile?.lastName ?? user?.lastName;
    if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
    if (firstName) return firstName[0].toUpperCase();
    // fallback: derive from email local part split by "."
    const parts = emailLabel?.split(".") ?? [];
    return (
      parts
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? "")
        .join("") || "?"
    );
  })();

  const visibleGroups = NAV_GROUPS.filter((g) => !g.adminOnly || admin)
    .map((g) => ({ ...g, items: g.items.filter((item) => !item.adminOnly || admin) }))
    .filter((g) => g.items.length > 0);

  return (
    <nav style={S.nav} data-testid="nav-sidebar">
      {/* Logo */}
      <div style={S.logo}>
        <div style={S.logoRow}>
          <div style={S.logoIcon}>G</div>
          <div style={S.logoTitle}>GTM Intelligence</div>
          <ChatIconButton onClick={() => setChatOpen((v) => !v)} />
        </div>
        <div style={S.logoSubtitle}>Axiom — Internal</div>
      </div>

      {/* User + view toggle */}
      {user && (
        <div style={S.userSection}>
          <div style={S.userRow}>
            <div style={S.userAvatarLg}>{navInitials}</div>
            <div style={S.userInfo}>
              <div style={S.userEmail}>{profile?.firstName ?? emailLabel ?? user.email}</div>
              <div style={S.userRole(isLeadership)}>
                {profile?.title ? profile.title.toUpperCase() : user.gtm_role}
              </div>
            </div>
          </div>

          {/* User filter dropdown */}
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              style={{ ...S.toggleBtn(true), width: "100%" }}
            >
              <span style={S.toggleOwnerText}>
                {ownerUserId
                  ? (workspaceUsers.find((u) => u.id === ownerUserId)?.name?.split(" ")[0] ??
                    workspaceUsers.find((u) => u.id === ownerUserId)?.email.split("@")[0] ??
                    "User")
                  : "Global"}
              </span>
              <span style={S.toggleCaret}>▾</span>
            </button>

            {/* Dropdown: Global + all workspace users */}
            {dropdownOpen && (
              <div style={S.dropdown}>
                {/* Global option */}
                <button
                  type="button"
                  onClick={() => {
                    setOwnerUserId(null);
                    setDropdownOpen(false);
                  }}
                  style={S.dropdownBtn(!ownerUserId)}
                >
                  <span style={{ ...S.avatar, fontSize: FONT_NANO }}>∞</span>
                  <span style={S.dropdownTextSpan}>Global</span>
                  {!ownerUserId && <span style={S.dropdownCheck}>✓</span>}
                </button>

                {workspaceUsers.map((u) => {
                  const isSelected = ownerUserId === u.id;
                  const isMe = u.email === user?.email;
                  const label = u.name || u.email.split("@")[0];
                  const parts = label.trim().split(/\s+/);
                  const initials =
                    parts.length >= 2
                      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
                      : (parts[0]?.[0]?.toUpperCase() ?? "?");
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setOwnerUserId(u.id);
                        setDropdownOpen(false);
                      }}
                      style={S.dropdownBtn(isSelected)}
                    >
                      <span style={S.avatar}>{initials}</span>
                      <span style={S.dropdownTextSpan}>
                        {label}
                        {isMe ? " (me)" : ""}
                      </span>
                      {isSelected && <span style={S.dropdownCheck}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Nav groups */}
      <div style={S.navScrollArea}>
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <div style={S.groupLabel}>{group.label}</div>
            <div style={S.groupItems}>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={{ pathname: item.to, search: ownerUserId ? `?user=${ownerUserId}` : "" }}
                  end={item.end ?? item.to === "/"}
                  style={({ isActive }) => S.navLink(isActive)}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* External links + sign out */}
      <div style={S.externalSection}>
        <div style={S.groupLabel}>Quick Links</div>
        {externalLinks.map((link) => (
          <a key={link.href} href={link.href} target="_blank" rel="noreferrer" style={S.extLink}>
            <span style={S.extLinkCaret}>↗</span>
            {link.label}
          </a>
        ))}
        <button type="button" style={S.signOut} onClick={logout}>
          <span style={S.signOutIcon}>⏻</span>
          Sign out
        </button>
      </div>
    </nav>
  );
}
